import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  SignalSlashIcon, 
  ServerIcon, 
  LockClosedIcon,
  CloudIcon,
  WifiIcon
} from '@heroicons/react/24/outline';

const OfflineIndicator = () => {
  const [isPulsing, setIsPulsing] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsPulsing(prev => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative">
      {/* Floating Offline Badge */}
      <motion.div
        className="fixed top-24 right-6 z-40 bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-full shadow-2xl border-2 border-green-400/30 backdrop-blur-sm"
        animate={{
          y: [0, -10, 0],
          scale: [1, 1.05, 1],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <div className="flex items-center space-x-2">
          <SignalSlashIcon className="h-4 w-4" />
          <span className="text-sm font-bold">100% OFFLINE</span>
          <div className={`w-2 h-2 rounded-full ${isPulsing ? 'bg-white' : 'bg-green-200'} transition-colors duration-500`} />
        </div>
      </motion.div>

      {/* Offline Status Banner */}
      <motion.div
        className="bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 border-y-2 border-green-500/30 py-6 overflow-hidden relative"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        {/* Animated Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-transparent"
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 border-2 border-green-500 mb-4"
            >
              <SignalSlashIcon className="h-8 w-8 text-green-400" />
            </motion.div>

            <motion.h2
              className="text-3xl font-bold text-white mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              The Only AI That Works
              <span className="block text-green-400 text-4xl font-bold mt-2">
                COMPLETELY OFFLINE
              </span>
            </motion.h2>

            <motion.p
              className="text-xl text-primary-200 max-w-3xl mx-auto mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              No internet connection required. No data sent to the cloud. Your privacy is absolute.
            </motion.p>

            {/* Feature Comparison */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-6 border border-green-500/20">
                <div className="flex items-center justify-center mb-4">
                  <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30">
                    <CloudIcon className="h-6 w-6 text-red-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Other AI Tools</h3>
                <p className="text-red-400 text-sm">Require constant internet connection</p>
                <p className="text-red-400 text-sm">Send your data to cloud servers</p>
                <p className="text-red-400 text-sm">Privacy concerns & data breaches</p>
              </div>

              <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-6 border border-green-500/20 relative">
                <motion.div
                  className="absolute inset-0 bg-green-500/10 rounded-xl"
                  animate={{
                    opacity: [0, 0.3, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                <div className="relative flex items-center justify-center mb-4">
                  <div className="p-3 rounded-lg bg-green-500/20 border border-green-500/30">
                    <LockClosedIcon className="h-6 w-6 text-green-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Speedr AI</h3>
                <p className="text-green-400 text-sm font-medium">✓ Works completely offline</p>
                <p className="text-green-400 text-sm font-medium">✓ Your data never leaves your device</p>
                <p className="text-green-400 text-sm font-medium">✓ 100% private & secure</p>
              </div>

              <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-6 border border-green-500/20">
                <div className="flex items-center justify-center mb-4">
                  <div className="p-3 rounded-lg bg-blue-500/20 border border-blue-500/30">
                    <ServerIcon className="h-6 w-6 text-blue-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Traditional Software</h3>
                <p className="text-primary-300 text-sm">Limited AI capabilities</p>
                <p className="text-primary-300 text-sm">Requires internet for updates</p>
                <p className="text-primary-300 text-sm">No AI processing locally</p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Animated Connection Lines */}
        <svg
          className="absolute inset-0 pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern
              id="offline-grid"
              x="0"
              y="0"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <circle
                cx="20"
                cy="20"
                r="1"
                fill="rgba(34, 197, 94, 0.3)"
              >
                <animate
                  attributeName="r"
                  values="1;2;1"
                  dur="3s"
                  repeatCount="indefinite"
                />
              </circle>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#offline-grid)" />
        </svg>
      </motion.div>

      {/* Floating Network Status Indicators */}
      <div className="fixed bottom-8 left-8 z-40 space-y-2">
        <motion.div
          className="bg-red-500/20 border border-red-500/30 rounded-lg px-3 py-2 flex items-center space-x-2 backdrop-blur-sm"
          animate={{ opacity: [1, 0.5, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <WifiIcon className="h-4 w-4 text-red-400" />
          <span className="text-xs text-red-400 font-medium">No Internet</span>
        </motion.div>
        
        <motion.div
          className="bg-green-500/20 border border-green-500/30 rounded-lg px-3 py-2 flex items-center space-x-2 backdrop-blur-sm"
          animate={{ opacity: [1, 0.5, 1] }}
          transition={{ duration: 2, repeat: Infinity, delay: 1 }}
        >
          <ServerIcon className="h-4 w-4 text-green-400" />
          <span className="text-xs text-green-400 font-medium">AI Active</span>
        </motion.div>
      </div>
    </div>
  );
};

export default OfflineIndicator;
